// This is the login page
// Users must log in before they can access the dashboard
// It sends credentials to the backend and stores the JWT token
// If MFA is enabled, shows a TOTP code entry step after password validation

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaEmail, setMfaEmail] = useState("");

  const navigate = useNavigate();

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios.post(`${API_URL}/auth/login`, formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      });

      // If MFA is required, show the TOTP code entry screen
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setMfaEmail(response.data.email);
        setLoading(false);
        return;
      }

      // No MFA — store token and redirect
      sessionStorage.setItem("token", response.data.access_token);
      sessionStorage.setItem("user", JSON.stringify(response.data.user));
      navigate("/portal");

    } catch (err) {
      setError("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaVerify(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/auth/mfa/verify?email=${encodeURIComponent(mfaEmail)}&code=${encodeURIComponent(mfaCode)}`
      );

      sessionStorage.setItem("token", response.data.access_token);
      sessionStorage.setItem("user", JSON.stringify(response.data.user));
      navigate("/");

    } catch (err) {
      setError("Invalid code. Please try again.");
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="w-full max-w-md">

        {/* Logo and title */}
        <div className="text-center mb-8">
          <img src="/logo-white.png" alt="AleroSys" className="h-16 mx-auto mb-4" />
          <p className="text-gray-400 mt-1 text-sm">by Alero Systems LLC</p>
          <p className="text-gray-500 mt-2 text-sm">
            {mfaRequired ? "Two-Factor Authentication" : "Sign in to your MSP portal"}
          </p>
        </div>

        <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">

          {error && (
            <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Step 1 — Email and password */}
          {!mfaRequired && (
            <form onSubmit={handleLogin}>
              <div className="mb-5">
                <label className="block text-gray-400 text-sm mb-2">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@yourcompany.com"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          )}

          {/* Step 2 — TOTP code entry */}
          {mfaRequired && (
            <form onSubmit={handleMfaVerify}>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🔐</span>
                </div>
                <p className="text-gray-400 text-sm">
                  Open your authenticator app and enter the 6-digit code for <span className="text-white font-medium">{mfaEmail}</span>
                </p>
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Authentication code</label>
                <input
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors text-center text-2xl tracking-widest font-mono"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
              <button
                type="button"
                onClick={() => { setMfaRequired(false); setMfaCode(""); setError(null); }}
                className="w-full mt-3 text-gray-400 hover:text-white text-sm transition-colors"
              >
                ← Back to login
              </button>
            </form>
          )}
        </div>

        {!mfaRequired && (
          <div className="text-center mt-6">
            <p className="text-gray-500 text-sm">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/request-access")}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                Request Access
              </button>
            </p>
          </div>
        )}

        <p className="text-center text-gray-600 text-sm mt-4">
          AleroSys · DriverWatch MSP Platform · by Alero Systems LLC
        </p>

      </div>
    </div>
  );
}